import type { Page } from '../../types.js';

/**
 * The "what am I buying" page. It has to answer three questions a product owner
 * asks in the first meeting — what it does, what I can change without a release,
 * and what is honestly missing — so those are the three sections, in that order.
 */
const page: Page = {
  slug: 'what-it-is',
  title: 'What Nuqi is',
  lede:
    'One platform, sold to many banks, reshaped by each of them through configuration rather than a fork. It embeds gold and silver trading in your product as a widget, as components, as an SDK or as a raw API.',
  group: 'Start here',
  blocks: [
    {
      kind: 'stats',
      items: [
        { value: 'SaaS', label: 'Commercial model', sub: 'One platform, configured per bank', accent: 'teal' },
        { value: '4', label: 'Integration shapes', sub: 'Widget, components, SDK, API', accent: 'blue' },
        { value: '12', label: 'Widgets in the catalogue', sub: 'Seven built, one stand-in, four reserved', accent: 'blue' },
        { value: '2', label: 'Currencies shipped', sub: 'AED and USD commission defaults', accent: 'amber' },
      ],
    },

    { kind: 'heading', text: 'The four shapes, and what each one costs you' },
    {
      kind: 'table',
      columns: ['', 'Widget', 'Components', 'SDK', 'API'],
      rows: [
        ['Who designs the screen', 'Us', 'Us', 'You', 'You'],
        ['Where it runs', 'A sandboxed frame on our origin', 'Inside your React tree', 'Inside your app', 'On your servers'],
        ['Who owns the trading logic', 'Us', 'Us', 'Us', 'You'],
        [
          'What you can restyle',
          'Colours, logo, fonts, radius, density, wording',
          'The same tokens, plus your own stylesheet cascade',
          'Everything — the screens are yours',
          'Everything',
        ],
        ['Who carries a rounding or pricing mistake', 'Us', 'Us', 'Us', 'You'],
        ['When we improve a screen', 'You get it without shipping', 'You update a package', 'Not applicable', 'Not applicable'],
        ['Typical first use', 'A pilot', 'A branded app that keeps our layout', 'A branded app you intend to own', 'Reconciliation and back-office'],
      ],
    },
    {
      kind: 'callout',
      tone: 'stop',
      title: 'Every shape needs one route on your backend',
      text: 'Including the widget. Your servers are what say "this customer is logged in", because that is the only thing standing between a customer and another customer\'s holdings. There is no integration where your backend is not involved. Budget for it in all four cases.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What you own and what we own' },
    {
      kind: 'table',
      columns: ['Responsibility', 'You', 'Nuqi'],
      rows: [
        ['Customer identity, KYC and login', 'Yes', 'No — we never authenticate your customers'],
        ['The screens a customer sees', 'Yes, unless you take ours', 'We supply them on the widget and component routes'],
        ['Brand, colours, wording, languages', 'You configure it', 'We apply it to every surface at once'],
        ['The two-sided metal price', 'No', 'Yes, from a vendor feed'],
        ['Your margin and your fees', 'You set them', 'We apply them on every quote'],
        ['Executing and settling a trade', 'No', 'Yes'],
        ['The record of who owns how much metal', 'No', 'Yes, in a database that holds only your bank'],
        ['Per-customer limits', 'You set them', 'We enforce them server-side on every order'],
        ['Customer support', 'Yes', 'We support your team, not your customers'],
      ],
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What you configure without a release' },
    {
      kind: 'cards',
      columns: 4,
      items: [
        { title: 'Brand', text: 'Display name, light and dark logos, favicon, an issuer disclosure line, and your support, terms and privacy links.', accent: 'teal' },
        { title: 'Theme', text: 'Two complete token sets for light and dark: accent, surfaces, text, positive and negative, radius, font family and scale, density.', accent: 'teal' },
        { title: 'Locale', text: 'Default and available locales, an explicit right-to-left list, and per-locale string overrides so the product can carry your name for it.', accent: 'teal' },
        { title: 'Widgets', text: 'Per widget: on or off, a title override, a variant, its position, and an options bag the widget validates itself.', accent: 'blue' },
        { title: 'Trading', text: 'Which metals, which currencies, the default currency, your commission legs, a flat fee, a percentage fee, and a hard switch that removes selling.', accent: 'amber' },
        { title: 'Limits', text: 'Minimum and maximum order, daily buy and sell caps per customer, how many quotes one customer may hold open, and how long a quote stays firm.', accent: 'amber' },
        { title: 'Features', text: 'Trade, portfolio, alerts, savings plans, delivery, payout, reports, live streaming and a biometric gate on high-value actions.', accent: 'blue' },
        { title: 'What it can never do', text: 'Grant a permission, relax a security control, or carry a secret. Configuration is presentation and entitlement only — no bank can theme its way past a control.', accent: 'rose' },
      ],
    },
    {
      kind: 'flow',
      accent: 'teal',
      steps: [
        { title: 'Change proposed', sub: 'By an operator' },
        { title: 'Second person approves', sub: 'Never the requester' },
        { title: 'Version increments', sub: 'Audit row written' },
        { title: 'Live surfaces told', sub: 'A stream frame' },
        { title: 'Everything re-themes', sub: 'No redeploy, no reload' },
      ],
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What is live and what is reserved' },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Read this before it reaches a business case',
      text: 'The reserved capabilities below are named, scoped, feature-flagged and given database tables. They are not implemented. Switching one on today produces a panel telling the customer it is unavailable, not a working feature.',
    },
    {
      kind: 'table',
      columns: ['Capability', 'Status', 'What exists today'],
      rows: [
        ['Live buy and sell rates for both metals', '**Live**', 'Streamed, or polled when streaming is off'],
        ['Buy by entering a cash amount', '**Live**', 'Firm quote, then execution against it'],
        ['Sell part or all of a holding', '**Live**', 'Switchable off per bank, and refused server-side when off'],
        ['Portfolio and average cost', '**Live**', 'Valued at the sell rate, not at a mid'],
        ['Order history', '**Live**', 'Cursor-paginated, newest first'],
        ['Price chart', 'Reserved', 'The chart slot renders the rate panel; there is no price history endpoint'],
        ['Price alerts', 'Reserved', 'Scope and tables exist; no notification path'],
        ['Recurring savings plans', 'Reserved', 'Scope and tables exist; no scheduling or mandate handling'],
        ['Physical delivery', 'Reserved', 'Scope exists; no fulfilment, logistics or address handling'],
        ['Cash payout', 'Reserved', 'Selling works; where the cash lands stays your existing arrangement'],
        ['Statements and exports', 'Reserved', 'History is visible in the app but not exportable'],
      ],
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'How it works',
          text: 'The path from a customer tapping buy to metal recorded in their name, without a line of code.',
          href: '/business/how-it-works',
          accent: 'teal',
        },
        {
          title: 'Surfaces',
          text: 'Web, widget, iOS, Android, Huawei and React Native — what each looks like and when to pick it.',
          href: '/business/surfaces',
          accent: 'blue',
        },
        {
          title: 'Pricing and commission',
          text: 'The commercial core: four legs, per metal and per currency, and which of them you control.',
          href: '/business/pricing-and-commission',
          accent: 'amber',
        },
      ],
    },
  ],
};

export default page;
